import AccreditedDentalOffices from "#/api/server/accreditedDentalOffices";
import { ExpertisesCovered, ExpertisesCoveredMaped } from "#/types";
import { maskPhone } from "#/utils/mask";
import {
  Box,
  Button,
  Loader,
  Paragraph,
  Spacing,
  Title,
  Select,
  toast,
  Icon,
} from "@sicredi/react";
import { useFormik } from "formik";
import { Fragment, useEffect, useState } from "react";
import * as yup from "yup";
import "./style.scss";
import Wrapper from "../Wrapper";
import { getSearchParams } from "#/utils/getSearchParams";

export const ERROR_DEFAULT = "Preencha esse campo para continuar";
export const HTTP_ERROR =
  "Ops! Tivemos um problema, tente novamente daqui a pouco";

export function handleExpertiseResponse(
  expertisesCovered: ExpertisesCovered[]
) {
  const expert: ExpertisesCoveredMaped = {};
  type Expertise = keyof typeof expert;
  for (let dentist of expertisesCovered) {
    const expertises = new Set(dentist.expertise);
    expertises.forEach((expertise) => {
      if (expert[expertise as Expertise]) {
        expert[expertise as Expertise].push(dentist);
        return;
      }
      expert[expertise] = [dentist];
    });
  }
  return expert;
}

export function filterDentistList(
  dentistList: ExpertisesCoveredMaped,
  filters: { neighborhood: string; expertise: string }
): ExpertisesCovered[] {
  const hasNeighborhoodFilter = !!filters.neighborhood;
  const hasExpertiseFilter = !!filters.expertise;

  function allUniqueDentistis(
    dentistList: ExpertisesCoveredMaped
  ): ExpertisesCovered[] {
    const allDentists: ExpertisesCovered[] = Object.values(dentistList).flat();

    const dentists = {} as { [name: string]: ExpertisesCovered };
    for (const dentist of allDentists) {
      if (dentists[dentist.name]) {
        continue;
      }
      dentists[dentist.name] = dentist;
    }
    return Object.values(dentists);
  }
  const filterDentistByNeighborhood = (dentist: ExpertisesCovered) =>
    dentist.neighborhood.toLowerCase().trim() ===
    filters.neighborhood.toLowerCase().trim();

  if (!hasExpertiseFilter && !hasNeighborhoodFilter) {
    return allUniqueDentistis(dentistList);
  }
  if (!hasNeighborhoodFilter) {
    return dentistList[filters.expertise];
  }
  if (!hasExpertiseFilter && hasNeighborhoodFilter) {
    return allUniqueDentistis(dentistList).filter(filterDentistByNeighborhood);
  }
  return dentistList[filters.expertise].filter(filterDentistByNeighborhood);
}

const initialSelectInputData = {
  label: "",
  value: "",
};

type SelectInput = typeof initialSelectInputData;

const validationSchema = yup.object().shape({
  state: yup.string().required(ERROR_DEFAULT),
  city: yup.string().required(ERROR_DEFAULT),
  expertise: yup.string().optional(),
  neighborhood: yup.string().optional(),
});

const initialValues = {
  state: getSearchParams("estado") ?? "",
  city: getSearchParams("cidade") ?? "",
  neighborhood: "",
  expertise: "",
};
const initialExpertisesCovered = [
  {
    name: "",
    expertise: [""],
    postalCode: "",
    street: "",
    neighborhood: "",
    state: "",
    city: "",
    phone: "",
    company: "",
  },
];
const initialDentist = {
  "": initialExpertisesCovered,
};

type FormInputs = {
  statesInput: SelectInput[];
  citiesInput: SelectInput[];
  neighborhoodsInput: SelectInput[];
  expertisesInput: SelectInput[];
};

const initialFormsValues = {
  statesInput: [initialSelectInputData],
  citiesInput: [initialSelectInputData],
  neighborhoodsInput: [initialSelectInputData],
  expertisesInput: [initialSelectInputData],
};

export const ShowAccreditedDentalOffices: React.FC<{ isModal?: boolean }> = ({
  isModal,
}) => {
  const iconInput = !isModal
    ? {
        icon: <Icon name="search" />,
      }
    : {};
  const [loading, setLoading] = useState<boolean>(false);
  const [renderDentistList, setRenderDentistList] = useState<boolean>(false);
  const [
    { statesInput, citiesInput, expertisesInput, neighborhoodsInput },
    setInputData,
  ] = useState<FormInputs>(initialFormsValues);
  const [coveredDentistsByExpertise, setCoveredDentistsByExpertise] =
    useState<ExpertisesCoveredMaped>(initialDentist);
  const [coveredDentistsRenderList, setCoveredDentistsRenderList] = useState<
    ExpertisesCovered[]
  >(initialExpertisesCovered);

  const {
    values,
    handleSubmit,
    submitCount,
    handleBlur,
    errors,
    setFieldValue,
    touched,
  } = useFormik<typeof initialValues>({
    onSubmit,
    initialValues,
    validationSchema,
  });

  function onSubmit() {
    try {
      setCoveredDentistsRenderList(initialExpertisesCovered);
      const filtredExpertises = filterDentistList(coveredDentistsByExpertise, {
        neighborhood: values.neighborhood,
        expertise: values.expertise,
      });
      setCoveredDentistsRenderList(filtredExpertises);
      setRenderDentistList(true);
    } catch (error) {
      toast.error(HTTP_ERROR);
    }
  }

  useEffect(() => {
    if (getSearchParams("estado")) {
      initPage();
    } else {
      fetchCoverageStates();
    }
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function initPage() {
    await fetchCoverageStates();
    await fetchCoverageByState(getSearchParams("estado"));
    if (getSearchParams("cidade")) {
      await fetchCoverageExpertises(getSearchParams("cidade"));
      await fetchCoveredNeighborhoods(
        getSearchParams("estado"),
        getSearchParams("cidade")
      );
      handleSubmit();
    }
  }

  async function fetchCoverageStates() {
    try {
      setLoading(true);
      const accreditedDentalOffices = new AccreditedDentalOffices("SULAMERICA");
      const coveredStatesResponse = await accreditedDentalOffices.getStates();
      const mapedCoveredStatesToSelectInput = coveredStatesResponse
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((state) => ({ label: state.name, value: state.acronym }));
      setInputData((oldState) => ({
        ...oldState,
        statesInput: mapedCoveredStatesToSelectInput,
      }));
    } catch (error) {
      toast.error(HTTP_ERROR);
    } finally {
      setLoading(false);
    }
  }

  async function fetchCoverageByState(state: string) {
    try {
      setLoading(true);
      const accreditedDentalOffices = new AccreditedDentalOffices("SULAMERICA");
      const coveredCitiesResponse =
        await accreditedDentalOffices.getStateCities(state);
      const mapedCoveredCitiesToSelectInput = [...coveredCitiesResponse]
        .sort()
        .map((city) => ({
          label: city,
          value: city,
        }));
      setInputData((oldState) => ({
        ...oldState,
        citiesInput: mapedCoveredCitiesToSelectInput,
      }));
    } catch (error) {
      toast.error(HTTP_ERROR);
    } finally {
      setLoading(false);
    }
  }

  async function fetchCoverageExpertises(city: string) {
    try {
      if (!values.state) return;
      setLoading(true);
      const accreditedDentalOffices = new AccreditedDentalOffices("SULAMERICA");
      const coveredExpertisesResponse =
        await accreditedDentalOffices.getExpertises(values.state, city);
      const coveredExperises = handleExpertiseResponse(
        coveredExpertisesResponse
      );
      const mapedCoveredExpestisesToSelectInput = Object.keys(coveredExperises)
        .sort()
        .map((expertise) => ({ label: expertise, value: expertise }));
      setInputData((oldState) => ({
        ...oldState,
        expertisesInput: mapedCoveredExpestisesToSelectInput,
      }));
      setCoveredDentistsByExpertise(coveredExperises);
    } catch (error) {
      toast.error(HTTP_ERROR);
    } finally {
      setLoading(false);
    }
  }

  async function fetchCoveredNeighborhoods(state: string, city: string) {
    try {
      setLoading(true);
      const accreditedDentalOffices = new AccreditedDentalOffices("SULAMERICA");
      const coveredNeighborhoodsResponse =
        await accreditedDentalOffices.getNeighborhoods(state, city);
      const mapedCoveredNeighborhoodsToSelectInput = [
        ...coveredNeighborhoodsResponse,
      ]
        .sort()
        .map((neighborhood) => ({
          label: neighborhood,
          value: neighborhood,
        }));
      setInputData((oldState) => ({
        ...oldState,
        neighborhoodsInput: mapedCoveredNeighborhoodsToSelectInput,
      }));
    } catch (error) {
      toast.error(HTTP_ERROR);
    } finally {
      setLoading(false);
    }
  }

  function resetInputs(referenceInput: keyof typeof values) {
    if (referenceInput === "state") {
      setFieldValue("city", "");
      setFieldValue("neighborhood", "");
      setFieldValue("expertise", "");
      return;
    }
    if (referenceInput === "city") {
      setFieldValue("neighborhood", "");
      setFieldValue("expertise", "");
      return;
    }
  }

  function resetDentistList() {
    setRenderDentistList(false);
    setCoveredDentistsRenderList(initialExpertisesCovered);
  }

  function validateExpertiseChange(expertise: string) {
    if (!expertise) return;
    if (!!values.neighborhood) {
      const hasDentist = coveredDentistsByExpertise[expertise].find(
        (dentist) => dentist.neighborhood === values.neighborhood
      );
      if (!hasDentist) {
        setFieldValue("neighborhood", "");
        toast.warn(`Não existe cobertura para esse bairro`);
      }
    }
  }

  return (
    <Fragment>
      {loading && <Loader data-testid="loader" show={true} fullScreen />}
      <form
        noValidate
        onSubmit={handleSubmit}
        data-testid="coverage-form"
        className={`coverage-form ${isModal ? "modal" : "not-modal"}`}
      >
        <Wrapper>
          <Select
            type="text"
            name="state"
            data-testid="state"
            label="Estado"
            data={statesInput}
            required
            onChange={(state) => {
              setFieldValue("state", state);
              fetchCoverageByState(state);
              resetInputs("state");
              resetDentistList();
            }}
            onBlur={handleBlur}
            value={values.state}
            errorMessage={
              (submitCount || touched.state) && errors.state && ERROR_DEFAULT
            }
            {...iconInput}
          />
          <Select
            type="text"
            name="city"
            data-testid="city"
            label="Cidade"
            data={citiesInput}
            required
            onChange={async (city) => {
              setRenderDentistList(false);
              setFieldValue("city", city);
              fetchCoverageExpertises(city);
              resetInputs("city");
              await fetchCoverageExpertises(city);
              await fetchCoveredNeighborhoods(values.state, city);
              handleSubmit();
            }}
            onBlur={handleBlur}
            value={values.city}
            errorMessage={
              (!!submitCount || touched.city) && errors.city && ERROR_DEFAULT
            }
            {...iconInput}
          />
          <Select
            type="text"
            name="neighborhood"
            data-testid="neighborhood"
            label="Bairro"
            data={neighborhoodsInput}
            onChange={(neighborhood) => {
              setFieldValue("neighborhood", neighborhood);
              if (isModal) {
                resetDentistList();
                return;
              }
              handleSubmit();
            }}
            onBlur={handleBlur}
            value={values.neighborhood}
          />
          <Select
            type="text"
            name="expertise"
            data-testid="expertise"
            label="Especialidade"
            data={expertisesInput}
            onChange={(expertise) => {
              setFieldValue("expertise", expertise);
              validateExpertiseChange(expertise);
              if (isModal) {
                resetDentistList();
                return;
              }
              handleSubmit();
            }}
            onBlur={handleBlur}
            value={values.expertise}
            errorMessage={
              (!!submitCount || touched.expertise) &&
              errors.expertise &&
              ERROR_DEFAULT
            }
          />
        </Wrapper>
        {isModal && (
          <>
            <Spacing appearance="medium" />
            <Button
              appearance="primary"
              type="submit"
              data-testid="seach-coverage"
              block
            >
              Buscar
            </Button>
          </>
        )}
      </form>
      {renderDentistList && (
        <>
          <Spacing appearance="small" />
          <Title as="h2" weight="bold">
            Rede credenciada
          </Title>
          <Spacing appearance="small" />
          <div
            className={`dentist-coverage-list ${
              isModal ? "modal" : "not-modal"
            }`}
          >
            {coveredDentistsRenderList.map((dentist, index) => (
              <Box
                data-testid="dentist-coverage-item"
                className="dentist-coverage-item"
                key={index}
              >
                <Paragraph size="x-small" weight="light">
                  {dentist.neighborhood}
                </Paragraph>
                <Spacing appearance="x-small" />
                <Title as="h2">{dentist.name}</Title>
                <Spacing appearance="x-small" />
                <Paragraph size="small" weight="bold">
                  {dentist.street}
                </Paragraph>
                <Paragraph size="small" weight="bold">
                  {maskPhone(dentist.phone || "")}
                </Paragraph>
              </Box>
            ))}
          </div>
        </>
      )}
    </Fragment>
  );
};
